import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  model,
  Output,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { ButtonComponent } from '@shared/components/button/button.component';
import { InputComponent } from '@shared/components/input/input.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { PermissionType } from '@shared/services/gql.service';
import { AuthService } from '@shared/store/auth/auth.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [InputComponent, FormsModule, ButtonComponent, TooltipDirective, NgClass],
  selector: 'aux-invoice-detail-invoice-notes',
  styles: [
    `
      :host ::ng-deep textarea {
        box-shadow: none !important;
      }
    `,
  ],
  template: `
    <div class="mb-4 text-xl font-semibold">Notes</div>

    <div class="flex rounded-lg bg-aux-gray-light p-4">
      <aux-input
        class="flex-grow"
        inputClassName="resize-none border-0 bg-transparent !outline-none !shadow-none w-full !max-w-full !text-lg text-aux-gray-darkest !font-normal"
        [textArea]="true"
        resize="none"
        [rows]="1"
        [(ngModel)]="notes"
        placeholder="Add a note..."
        [textareaAutoresize]="true"
        [disabled]="!editMode()"
      />

      <div class="flex w-[150px] min-w-[150px] items-start justify-end pt-0.5">
        @if (editMode()) {
          <aux-button
            class="mr-2"
            variant="primary"
            label="Save"
            data-pendo-id="invoice-notes-save-button"
            (click)="onSave()"
          />

          <aux-button variant="tertiary" label="Cancel" (click)="onCancel()" />
        } @else {
          <aux-button
            variant="custom"
            icon="Pencil"
            svgAttributes="text-aux-blue"
            classList="py-0.5 mt-[5px]"
            [ngClass]="{
              'opacity-50': !hasEditPermission(),
            }"
            type="button"
            [auxTooltip]="!hasEditPermission() ? disabledTooltip : ''"
            [disabled]="!hasEditPermission()"
            (click)="onEdit()"
          />
        }
      </div>
    </div>
  `,
})
export class InvoiceNotesComponent {
  authService = inject(AuthService);

  editMode = signal(false);

  notes = model.required<string>();

  textarea = viewChild.required(InputComponent);

  @Output() cancelNotes = new EventEmitter();
  disabledTooltip = MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
  hasEditPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_UPDATE_INVOICES],
  });
  @Output() saveNotes = new EventEmitter();

  onCancel() {
    this.cancelNotes.emit();
    this.editMode.set(false);
  }

  onEdit() {
    this.editMode.set(true);
    const cmp = this.textarea();

    if (!cmp.input) {
      return;
    }

    setTimeout(() => {
      const end = cmp.input?.nativeElement.value.length || 0;

      cmp.input?.nativeElement.focus();
      cmp.input?.nativeElement.setSelectionRange(end, end);
    });
  }

  onSave() {
    this.saveNotes.emit();
    this.editMode.set(false);
  }
}
